import { isValidHandle, normalizeHandle, flagEmoji } from "@shared/flag";
import {
  generateOwnerToken,
  getStoredHandle,
  getStoredToken,
  persistIdentity,
} from "./token";
import { apiClaim, apiHandleAvailable, apiSignIn } from "./api";

export type Identity = {
  handle: string;
  countryCode: string;
  flag: string;
};

export type JoinOutcome =
  | { kind: "joined"; identity: Identity }
  | { kind: "taken" }
  | { kind: "invalid" }
  | { kind: "error"; message: string };

export async function autoSignIn(): Promise<Identity | null> {
  const handle = getStoredHandle();
  if (!handle) return null;
  // Token is optional now — the server accepts signin for any valid
  // handle and the ownerHash is no longer a gate. Pass whatever we have.
  const token = getStoredToken() ?? "";
  const res = await apiSignIn(handle, token);
  if (!res.ok) return null;
  return {
    handle: res.handle,
    countryCode: res.countryCode,
    flag: flagEmoji(res.countryCode),
  };
}

export async function checkAvailability(handle: string): Promise<boolean> {
  const h = normalizeHandle(handle);
  if (!isValidHandle(h)) return false;
  return apiHandleAvailable(h);
}

export async function claim(rawHandle: string): Promise<JoinOutcome> {
  const handle = normalizeHandle(rawHandle);
  if (!isValidHandle(handle)) return { kind: "invalid" };

  // Handles are no longer exclusive. We still call /api/handle/claim so
  // the server can register the row (for country lookup + bookkeeping),
  // but we treat any non-success as a soft failure and fall back to a
  // locally-constructed identity so the user isn't blocked.
  const token = generateOwnerToken();
  persistIdentity(handle, token);
  try {
    const res = await apiClaim(handle, token);
    if (res.ok) {
      return {
        kind: "joined",
        identity: {
          handle: res.handle,
          countryCode: res.countryCode,
          flag: flagEmoji(res.countryCode),
        },
      };
    }
    if (res.reason === "invalid") return { kind: "invalid" };
  } catch {
    /* fall through to local identity */
  }
  return {
    kind: "joined",
    identity: {
      handle,
      countryCode: "",
      flag: flagEmoji(""),
    },
  };
}

export async function recover(
  rawHandle: string,
  token: string,
): Promise<JoinOutcome> {
  const handle = normalizeHandle(rawHandle);
  if (!isValidHandle(handle)) return { kind: "invalid" };
  const res = await apiSignIn(handle, token);
  if (!res.ok) return { kind: "error", message: res.reason };
  persistIdentity(res.handle, token);
  return {
    kind: "joined",
    identity: {
      handle: res.handle,
      countryCode: res.countryCode,
      flag: flagEmoji(res.countryCode),
    },
  };
}
